import "./Business.scss";
import { useEffect, useState, useMemo, useCallback } from "react";
import CustomCard from "../../components/CustomCard/CustomCard";
import { useSelector, useDispatch } from "react-redux";
import useSearchableTable from "../../hooks/useSearchableTable";
import useUserPrivileges from "../../hooks/useUserPrivileges";
import useDocDownloadError from "../../hooks/useDocDownloadError";
import TableHeader from "../../components/CustomTable/TableHeader";
import { getStatusList } from "../../redux/slices/EnqMngtSlice";
import { getBusinessView } from "../../redux/slices/BusSlice";
import BusTable from "./Components/BusTable";
import { capitalizeEntireWord, downloadPageHandle, generateStatusTableTitle } from "../../helpers/helpers";
import { BUS_STATUS_DISPLAY_NAMES, STATUS } from "../../constants/constants";
import { BUS_STATUS_PRIVILEGE_MAP } from "../../constants/PermissionCodes";
import { count } from "../../redux/slices/dealsSlice";

const IS_DEAL_STATUS_IDS = Object.values(STATUS).map((s) => s.ID);

const buildStatusTableTitle = ({
  selectedStatus,
  statusDisplayNames = {},
  statusListWithCounts = [],
  defaultTitle = "",
}) => {
  const getCount = (id) => {
    const item = statusListWithCounts?.find((s) => s.statusId === id);
    return Number(item?.count) || 0;
  };

  if (Array.isArray(selectedStatus)) {
    if (selectedStatus.length === 0) {
      return `${capitalizeEntireWord(defaultTitle) || defaultTitle} LIST (0)`;
    }
    return selectedStatus
      .map((id, index) => {
        const rawName = statusDisplayNames?.[id] || id;
        const name = capitalizeEntireWord(rawName) || rawName || "";
        const count = getCount(id);
        const isLast = index === selectedStatus.length - 1;
        return isLast ? `${name} LIST (${count})` : `${name} (${count})`;
      })
      .join(", ");
  }

  if (selectedStatus) {
    const rawName = statusDisplayNames?.[selectedStatus] || selectedStatus || defaultTitle;
    const name = capitalizeEntireWord(rawName) || rawName || "";
    const count = getCount(selectedStatus);
    return `${name} LIST (${count})`;
  }

  return `${capitalizeEntireWord(defaultTitle) || defaultTitle} LIST (0)`;
};

const Business = () => {
  const dispatch = useDispatch();
  const { CountData } = useSelector((state) => state?.deals);
  const { statusList } = useSelector((state) => state?.enqMngt);
  const docLoading = useDocDownloadError();
  const { businessData } = useSelector((state) => state?.business);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [columnControl, setColumnControl] = useState(null);
  const { allowedStatusIds } = useUserPrivileges(BUS_STATUS_PRIVILEGE_MAP);

const DEFAULT_BUS_STATUSES = [STATUS.ACTIVE.ID];

  useEffect(() => {
    const fetchStatus = async () => {
      const res = await dispatch(getStatusList("bus"));
      if (res?.payload?.statusCode === 200) {
        const preferredStatuses = DEFAULT_BUS_STATUSES.filter((id) =>
          allowedStatusIds.includes(id),
        );
        setSelectedStatus(
          preferredStatuses.length > 0
            ? preferredStatuses
            : allowedStatusIds[0]
            ? [allowedStatusIds[0]]
            : [],
        );
      }
    };
    fetchStatus();
  }, [dispatch, allowedStatusIds]);

  useEffect(() => {
    if (selectedStatus) {
      dispatch(getBusinessView(selectedStatus));
    }
  }, [dispatch, selectedStatus]);

  useEffect(() => {
    dispatch(count("BUSINESS"));
  }, [dispatch]);

  const { searchText, setSearchText, filteredData } = useSearchableTable(businessData);

  const handleStatusChange = useCallback((value) => {
    setSelectedStatus(value);
  }, []);

  const filteredStatusList = useMemo(
    () => statusList?.filter((status) => allowedStatusIds.includes(status.statusId)) ?? [],
    [statusList, allowedStatusIds],
  );

  const statusListWithCounts = useMemo(
    () => filteredStatusList.map((item) => {
      const isDeal = IS_DEAL_STATUS_IDS.includes(item.statusId);
      return {
        ...item,
        count: isDeal ? CountData?.find((c) => c.statusId === item.statusId)?.count || 0 : undefined,
      };
    }),
    [filteredStatusList, CountData],
  );

  const handleDownload = useCallback(
    (type) => downloadPageHandle({ dispatch, type, screenName: "business", statusId: selectedStatus }),
    [dispatch, selectedStatus],
  );

  const handleDownloadPdf = useCallback(() => handleDownload("pdf"), [handleDownload]);
  const handleDownloadExcel = useCallback(() => handleDownload("excel"), [handleDownload]);

  const tableTitle = useMemo(
    () =>
      (generateStatusTableTitle || buildStatusTableTitle)({
        selectedStatus,
        statusDisplayNames: BUS_STATUS_DISPLAY_NAMES,
        statusListWithCounts,
        defaultTitle: "Business",
      }),
    [selectedStatus, statusListWithCounts],
  );

  return (
    <CustomCard>
      <TableHeader
        title={tableTitle}
        searchText={searchText}
        setSearchText={setSearchText}
        isDropdown={true}
        dropdownData={statusListWithCounts}
        selectedStatus={selectedStatus}
        handleStatusChange={handleStatusChange}
        statusDisplayNames={BUS_STATUS_DISPLAY_NAMES}
        isDownloadRequired={true}
        onDownloadPdf={handleDownloadPdf}
        onDownloadExcel={handleDownloadExcel}
        downloadLoading={docLoading}
        columnControl={columnControl}
      />
      <BusTable
        tableData={filteredData}
        selectedStatus={selectedStatus}
        onColumnControlChange={setColumnControl}
      />
    </CustomCard>
  );
};

export default Business;